import { ButtonVariant, CoButton } from "components/CoButton";
import { groupHistoriesByDate } from "features/History/utils/histories";
import useThreads from "features/Sidebar/hooks/useThreads";
import useLabels from "hooks/useLabels";
import { useRouter } from "next/router";
import { useMemo } from "react";
import HistoryGroup from "./components/HistoryGroup";
import useHistoryActions from "./hooks/useHistoryActions";

const MAX_SIDEBAR_HISTORY_ITEMS = 5;

type HistoryProps = {
  onNavigate: () => void;
};

/**
 * @component History
 * @description Shows recent threads in the sidebar. Each item can be clicked to navigate to that thread.
 */
function History({ onNavigate }: HistoryProps) {
  const router = useRouter();
  const [searchHistoryLabels] = useLabels("screens.sidebar.history");

  const { fetchNextPage, isError, isLoading, threads } = useThreads(true);

  const groupedData = useMemo(
    () => groupHistoriesByDate([], threads ?? [], router.query),
    [threads, router.query]
  );

  const { handleClickHistory, handleSaveHistory, handleDeleteHistory } =
    useHistoryActions();

  if (isError || groupedData === undefined) {
    return (
      <div className="flex flex-col gap-2 items-center m-5 mx-8 text-center">
        {searchHistoryLabels["error"]}
        <CoButton
          variant={ButtonVariant.Secondary}
          onClick={() => fetchNextPage()}
          label={searchHistoryLabels["retry"]}
        />
      </div>
    );
  }

  if (isLoading) {
    return null;
  }

  const todayAndLastSevenDays = [
    ...groupedData.today,
    ...groupedData.lastSevenDays,
  ].slice(0, MAX_SIDEBAR_HISTORY_ITEMS);

  return (
    <div className="flex pl-4">
      <div className="flex w-5 h-full py-2 justify-center -mr-0.5">
        <div className="w-px h-full bg-bg-subtle" />
      </div>
      {!isLoading && groupedData.count !== 0 && (
        <HistoryGroup
          histories={todayAndLastSevenDays}
          onClick={(history) => handleClickHistory(history, onNavigate)}
          onDelete={handleDeleteHistory}
          onSave={handleSaveHistory}
        />
      )}
    </div>
  );
}

export default History;
